import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import { Sliders, Save, Edit, RotateCcw } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Ride Match Settings — edit the acting-driver matching engine's runtime config
 * (search radii, ranking weights, offer timeout, retries) without a restart.
 *
 * The form is metadata-driven: the engine returns a `fields` list describing each
 * tunable (kind, label, min/max, help), so adding a field on the engine surfaces
 * here automatically.
 */
const RideMatchSettings = () => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('settings', 'write');

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});

  const { data, isLoading, error, refetch } = useQuery(
    ['ridematch-settings'],
    () => api.get('/ridematch-settings'),
    { refetchOnWindowFocus: false },
  );

  const fields = data?.data?.fields || [];
  const effective = data?.data?.effective || {};

  // Seed the form from the effective values whenever they (re)load.
  useEffect(() => {
    if (!fields.length) return;
    const seed = {};
    fields.forEach((f) => {
      if (f.kind === 'radii') {
        seed[f.key] = (effective[f.key] || []).join(', ');
      } else if (f.kind === 'weights') {
        const w = effective[f.key] || {};
        seed[f.key] = {};
        (f.weight_keys || []).forEach((k) => { seed[f.key][k] = w[k] ?? 0; });
      } else {
        seed[f.key] = effective[f.key];
      }
    });
    setForm(seed);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMutation = useMutation(
    (values) => api.put('/ridematch-settings', { values }),
    {
      onSuccess: () => {
        toast.success('Matching config saved — takes effect within seconds');
        setIsEditing(false);
        refetch();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || 'Failed to save config');
      },
    },
  );

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const setWeight = (key, wk, value) =>
    setForm((p) => ({ ...p, [key]: { ...p[key], [wk]: value } }));

  const handleSave = () => {
    // Build the payload the engine expects: radii as an array, weights as numbers.
    const values = {};
    fields.forEach((f) => {
      if (f.kind === 'radii') {
        values[f.key] = String(form[f.key] || '')
          .split(',').map((s) => parseFloat(s.trim())).filter((n) => !Number.isNaN(n));
      } else if (f.kind === 'weights') {
        const w = {};
        Object.entries(form[f.key] || {}).forEach(([k, v]) => { w[k] = parseFloat(v) || 0; });
        values[f.key] = w;
      } else {
        values[f.key] = Number(form[f.key]);
      }
    });
    updateMutation.mutate(values);
  };

  const handleCancel = () => {
    setIsEditing(false);
    refetch(); // discard edits, reload effective values
  };

  const resetField = (f) => {
    if (f.kind === 'radii') setField(f.key, (f.default || []).join(', '));
    else if (f.kind === 'weights') {
      const w = {};
      (f.weight_keys || []).forEach((k) => { w[k] = (f.default || {})[k] ?? 0; });
      setField(f.key, w);
    } else setField(f.key, f.default);
  };

  if (isLoading) {
    return <div className="loading-spinner" style={{ margin: '4rem auto' }} />;
  }
  if (error) {
    // The dashboard server always answers with JSON { message }. No message means
    // the request never reached it (e.g. the proxy's 503 while the server is down),
    // so don't blame the engine for that.
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    const title = !message
      ? 'Could not reach the dashboard server.'
      : status === 403
        ? 'You do not have access to Ride Match Settings.'
        : 'Could not reach the matching engine.';
    return (
      <div className="empty-state">
        <p>{title}</p>
        <p className="text-sm text-gray-500">
          {message || `Request failed${status ? ` (HTTP ${status})` : ''}. Is the dashboard backend running?`}
        </p>
      </div>
    );
  }

  const disabled = !isEditing || !canWrite;

  return (
    <div>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Sliders className="h-6 w-6" /> Ride Match Settings
          </h1>
          <p className="page-subtitle">
            Acting-driver matching engine — search distance, ranking weights, timeouts.
            Changes apply live, no restart.
          </p>
        </div>
        {canWrite && !isEditing && (
          <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
            <Edit className="h-4 w-4 mr-2" /> Edit
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div className="card" key={f.key}>
            <div className="card-body">
              <div className="flex items-start justify-between">
                <label className="form-label">{f.label}</label>
                {isEditing && canWrite && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    title="Reset to .env default"
                    onClick={() => resetField(f)}
                  >
                    <RotateCcw className="h-3 w-3" />
                  </button>
                )}
              </div>

              {f.kind === 'weights' ? (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {(f.weight_keys || []).map((wk) => (
                    <div key={wk}>
                      <span className="text-xs text-gray-500 capitalize">
                        {wk.replace(/_/g, ' ')}
                      </span>
                      <input
                        type="number" step="0.05" min={f.min} max={f.max}
                        className="input"
                        value={form[f.key]?.[wk] ?? 0}
                        disabled={disabled}
                        onChange={(e) => setWeight(f.key, wk, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              ) : f.kind === 'radii' ? (
                <input
                  type="text"
                  className="input mt-1"
                  placeholder="e.g. 3, 5, 8"
                  value={form[f.key] ?? ''}
                  disabled={disabled}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              ) : (
                <input
                  type="number"
                  className="input mt-1"
                  min={f.min} max={f.max}
                  step={f.kind === 'float' ? '0.1' : '1'}
                  value={form[f.key] ?? ''}
                  disabled={disabled}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              )}

              <p className="text-xs text-gray-500 mt-1">{f.help}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                default:{' '}
                {f.kind === 'weights'
                  ? Object.entries(f.default || {}).map(([k, v]) => `${k} ${v}`).join(', ')
                  : Array.isArray(f.default) ? f.default.join(', ') : String(f.default)}
                {f.min != null && f.max != null ? `  ·  range ${f.min}–${f.max}` : ''}
              </p>
            </div>
          </div>
        ))}
      </div>

      {isEditing && canWrite && (
        <div className="flex items-center gap-2 mt-4">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={updateMutation.isLoading}
          >
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isLoading ? 'Saving…' : 'Save Changes'}
          </button>
          <button className="btn btn-outline" onClick={handleCancel}>Cancel</button>
        </div>
      )}
    </div>
  );
};

export default RideMatchSettings;
